import React from 'react';
import { observer } from 'mobx-react';
import { Col, Descriptions, Divider, Row, Table } from 'antd';
import { ShowPackageTypeTag } from './ShowPackageTypeTag';
import { ITariffPackageInstance, PackageType } from '../../../interfaces';
import { usePagesStoreThisListRight, useTariffPackageInstanceList, useUiIsLoading } from '../../../hooks';
import { dateText2Utc, showPercent, timeHumanFormat } from '../../../instruments';
import { tT } from '../../../translate';
import { Catcher, LinkComponent, TableItemToolbar, columnToolbar } from '../../../Components';
import ViewStyles from '../../styles/m_viewStyles.less';

export const TariffPackageInstanceTableList: React.FC = observer(() => {
    const isLoading = useUiIsLoading();
    const right = usePagesStoreThisListRight();
    const tariffPackageInstanceList = useTariffPackageInstanceList();

    const expandedRowRender = (record: ITariffPackageInstance): JSX.Element => {
        const cardInfo = record.cardId as never as { id: string; name: string };

        return (
            <div style={{ padding: '16px' }}>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Descriptions title="Usage Information" size="small" column={1} bordered>
                            <Descriptions.Item label="Data available">
                                {record?.dataVolumeAvailable} {record?.dataVolumeUnit}
                            </Descriptions.Item>
                            <Descriptions.Item label="SMS available">{record?.smsVolumeAvailable}</Descriptions.Item>
                            <Descriptions.Item label="Voice available">
                                {timeHumanFormat(record?.voiceVolumeAvailable)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data Usage %">
                                {showPercent(record?.dataVolumePercentageUsage)}
                            </Descriptions.Item>
                            <Descriptions.Item label="SMS Usage %">
                                {showPercent(record?.smsVolumePercentageUsage)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Voice Usage %">
                                {showPercent(record?.voiceVolumePercentageUsage)}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                    <Col span={12}>
                        <Descriptions title="Package Details" size="small" column={1} bordered>
                            <Descriptions.Item label="Package Type">
                                {record?.packageType?.toUpperCase()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Renewable">{record?.renewable ? 'Yes' : 'No'}</Descriptions.Item>
                            <Descriptions.Item label="Non-Expirable">
                                {record?.nonExpirable ? 'Yes' : 'No'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Out of Bundle">
                                {record?.outOfBundle ? 'Yes' : 'No'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Card">
                                <LinkComponent
                                    currentListComponent={'CardList'}
                                    id={cardInfo?.id}
                                    name={cardInfo?.name}
                                    isLight
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Created At">
                                {record?.createdAt ? new Date(record?.createdAt).toLocaleDateString() : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>

                <Divider />

                {record?.tariffPackage?.operators && record?.tariffPackage?.operators.length > 0 && (
                    <>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div>
                                    <strong>{tT('Tariff Package Operators')}:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                        {record?.tariffPackage?.operators.map((op) => (
                                            <span key={op?.id} style={{ marginRight: '8px' }}>
                                                <LinkComponent
                                                    currentListComponent={'OperatorsList'}
                                                    id={op?.id}
                                                    isLight
                                                    name={op?.name}
                                                />
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div>
                                    <strong>Tariff Package Networks:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                        {record?.tariffPackage?.networks?.map((net) => (
                                            <span key={net.id} style={{ marginRight: '8px' }}>
                                                <LinkComponent
                                                    currentListComponent={'OperatorsList'}
                                                    id={net.id}
                                                    isLight
                                                    name={net.name}
                                                />
                                                ,
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        );
    };

    const expandable = {
        expandedRowRender: expandedRowRender,
    };

    const tableColumns = [
        {
            title: 'Instance',
            dataIndex: 'instanceLink',
        },
        {
            title: 'Tariff Package',
            dataIndex: 'tariffPackage',
            key: 'tariffPackageName',
            render: (value: { id: string; name: string }) => (
                <LinkComponent
                    currentListComponent={'TariffPackagesToBuyList'}
                    id={value?.id}
                    isLight
                    name={value?.name}
                />
            ),
        },
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'companyName',
            render: (value: { id: string; name: string }) => (
                <LinkComponent currentListComponent={'CompaniesList'} id={value?.id} isLight name={value?.name} />
            ),
        },
        {
            title: 'Package Type',
            dataIndex: 'packageType',
            key: 'packageType',
            render: (value: string) => <ShowPackageTypeTag value={value as PackageType} />,
        },
        {
            title: 'Data Usage %',
            dataIndex: 'dataVolumePercentageUsage',
            key: 'dataVolumePercentageUsage',
            render: (value: number) => showPercent(value),
        },
        {
            title: 'SMS Usage %',
            dataIndex: 'smsVolumePercentageUsage',
            key: 'smsVolumePercentageUsage',
            render: (value: number) => showPercent(value),
        },
        {
            title: 'Voice Usage %',
            dataIndex: 'voiceVolumePercentageUsage',
            key: 'voiceVolumePercentageUsage',
            render: (value: number) => showPercent(value),
        },
        {
            title: 'Card',
            dataIndex: 'cardId',
            key: 'card',
            render: (value: { id: string; name: string }) => (
                <LinkComponent currentListComponent={'CardList'} id={value?.id} isLight name={value?.name} />
            ),
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'start_date',
            render: (value: string) => `${dateText2Utc(value)}`,
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'end_date',
            render: (value: string) => `${dateText2Utc(value)}`,
        },
        columnToolbar,
    ];

    const tableData = tariffPackageInstanceList.map((item: ITariffPackageInstance) => {
        const isPackageTypePool = item.packageType === 'pool';
        return {
            key: item.id,
            ...item,
            InstanceIdLink: item.id,
            instanceLink: (
                <LinkComponent
                    id={isPackageTypePool ? item.id : undefined}
                    name={!isPackageTypePool ? `# ${item.id}` : undefined}
                    isLight
                />
            ),
            tariffPackageName: item.tariffPackage?.name || '',
            companyName: item.companyName || '',
            toolbar: <TableItemToolbar {...right} id={item.id} isView={isPackageTypePool} isDelete={false} />,
        };
    });

    return (
        <Catcher>
            <div className={ViewStyles.tablePlace}>
                <Table
                    dataSource={tableData}
                    columns={tableColumns}
                    expandable={expandable}
                    pagination={false}
                    className={'table-theme'}
                    size={'middle'}
                    loading={isLoading}
                />
            </div>
        </Catcher>
    );
});
